import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Plus, 
  MessageSquare, 
  Mail, 
  Smartphone,
  Bell,
  Edit,
  RefreshCw,
  Loader2,
  Download,
  Upload,
  Save,
  Eye,
  Copy,
  Trash2,
  FileText,
  Code
} from 'lucide-react';
import { toast } from 'sonner';
import PageHelp from '../components/PageHelp';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// System templates that are used by the backend
const SYSTEM_TEMPLATES = [
  {
    id: 'invitation',
    name: 'Verification Invitation',
    type: 'system',
    channel: 'email',
    description: 'Sent when a new verification invitation is created',
    subject: 'Verify Your Identity for Edomatch',
    body: `Hello {{applicant_name}},

You've been invited to verify your identity for Edomatch.

This is a quick and secure process that will only take a few minutes. You'll need:
• A valid government-issued ID document
• A smartphone or webcam for selfie verification

Click the button below to start the verification process.

{{verification_link}}

This link will expire in 48 hours.

If you have any questions, please contact our support team.

Warm regards,
The Edomatch Team`,
    placeholders: ['applicant_name', 'verification_link']
  },
  {
    id: 'submission_received',
    name: 'Submission Received',
    type: 'system',
    channel: 'email',
    description: 'Sent when a candidate completes their verification submission',
    subject: 'Verification Submitted - What Happens Next',
    body: `Hello {{applicant_name}},

Thank you for completing your identity verification. We've received your documents and they are now being reviewed.

What happens next?
1. Document Review - Our team will verify your submitted documents
2. Identity Verification - We'll confirm your identity using our secure verification process  
3. Notification - You'll receive an email once the verification is complete

This usually takes 1-2 business days.

If you have any questions, please don't hesitate to reach out.

Warm regards,
The Edomatch Team`,
    placeholders: ['applicant_name']
  },
  {
    id: 'additional_info',
    name: 'Additional Information Request',
    type: 'system',
    channel: 'email',
    description: 'Sent when additional documents or information is needed',
    subject: 'Action Required: Additional Information Needed',
    body: `Hello {{applicant_name}},

We've reviewed your verification submission but need some additional information to complete the process.

What we need:
{{required_items}}

Please click the button below to provide the additional information. This link will expire in 72 hours.

{{additional_info_link}}

If you have any questions, please contact our support team.

Warm regards,
The Edomatch Team`,
    placeholders: ['applicant_name', 'required_items', 'additional_info_link']
  },
  {
    id: 'approved',
    name: 'Verification Approved',
    type: 'system',
    channel: 'email',
    description: 'Sent when identity verification is approved',
    subject: 'Identity Verification Approved',
    body: `Hello {{applicant_name}},

Great news! Your identity verification has been successfully completed and approved.

You can now proceed with your next steps on Edomatch.

If you have any questions, please don't hesitate to reach out.

Warm regards,
The Edomatch Team`,
    placeholders: ['applicant_name']
  },
  {
    id: 'rejected',
    name: 'Verification Rejected',
    type: 'system',
    channel: 'email',
    description: 'Sent when identity verification is rejected',
    subject: 'Verification Result - Action Required',
    body: `Hello {{applicant_name}},

We were unable to verify your identity based on the documents provided.

Reason: {{rejection_reason}}

If you believe this is an error, you may contact our support team with additional documentation.

Warm regards,
The Edomatch Team`,
    placeholders: ['applicant_name', 'rejection_reason']
  },
  {
    id: 'under_review',
    name: 'Under Review Status',
    type: 'system',
    channel: 'email',
    description: 'Sent when a case is flagged for additional review',
    subject: 'Your Identity Verification Status Update',
    body: `Hello {{applicant_name}},

Your submission has been flagged for additional review. Our team may contact you for further information.

We appreciate your patience during this process.

Warm regards,
The Edomatch Team`,
    placeholders: ['applicant_name']
  },
  {
    id: 'custom_message',
    name: 'Custom Agent Message',
    type: 'system',
    channel: 'email',
    description: 'Template for custom messages sent by verification agents',
    subject: '{{custom_subject}}',
    body: `Hello {{applicant_name}},

{{custom_message}}

If you have any questions, please don't hesitate to reach out.

Warm regards,
The Edomatch Team`,
    placeholders: ['applicant_name', 'custom_subject', 'custom_message']
  },
  // SMS Templates
  {
    id: 'sms_invitation',
    name: 'SMS - Verification Invitation',
    type: 'system',
    channel: 'sms',
    description: 'SMS sent when a new verification invitation is created',
    subject: '',
    body: `Hi {{applicant_name}}, you've been invited to verify your identity for Edomatch. Complete verification here: {{verification_link}} - Link expires in 48hrs.`,
    placeholders: ['applicant_name', 'verification_link']
  },
  {
    id: 'sms_submission_received',
    name: 'SMS - Submission Received',
    type: 'system',
    channel: 'sms',
    description: 'SMS sent when verification is submitted',
    subject: '',
    body: `Hi {{applicant_name}}, we've received your verification documents. Review takes 1-2 business days. We'll notify you once complete. - Edomatch`,
    placeholders: ['applicant_name']
  },
  {
    id: 'sms_additional_info',
    name: 'SMS - Additional Info Request',
    type: 'system',
    channel: 'sms',
    description: 'SMS sent when additional documents are needed',
    subject: '',
    body: `Hi {{applicant_name}}, we need additional info for your verification. Please submit here: {{additional_info_link}} - Link expires in 72hrs. - Edomatch`,
    placeholders: ['applicant_name', 'additional_info_link']
  },
  {
    id: 'sms_approved',
    name: 'SMS - Verification Approved',
    type: 'system',
    channel: 'sms',
    description: 'SMS sent when verification is approved',
    subject: '',
    body: `Hi {{applicant_name}}, great news! Your identity verification has been approved. You can now proceed with Edomatch. - Edomatch Team`,
    placeholders: ['applicant_name']
  },
  {
    id: 'sms_rejected',
    name: 'SMS - Verification Rejected',
    type: 'system',
    channel: 'sms',
    description: 'SMS sent when verification is rejected',
    subject: '',
    body: `Hi {{applicant_name}}, we were unable to verify your identity. Reason: {{rejection_reason}}. Contact support for assistance. - Edomatch`,
    placeholders: ['applicant_name', 'rejection_reason']
  },
  {
    id: 'sms_reminder',
    name: 'SMS - Verification Reminder',
    type: 'system',
    channel: 'sms',
    description: 'SMS reminder for pending verifications',
    subject: '',
    body: `Hi {{applicant_name}}, reminder: Your Edomatch verification is pending. Complete it here: {{verification_link}} before it expires. - Edomatch`,
    placeholders: ['applicant_name', 'verification_link']
  },
  {
    id: 'sms_custom',
    name: 'SMS - Custom Message',
    type: 'system',
    channel: 'sms',
    description: 'Template for custom SMS sent by verification agents',
    subject: '',
    body: `Hi {{applicant_name}}, {{custom_message}} - Edomatch Team`,
    placeholders: ['applicant_name', 'custom_message']
  }
];

export function Templates() {
  const [templates, setTemplates] = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState('system');
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    language: 'en',
    country: '',
    channel: 'email',
    subject: '',
    body: '',
    placeholders: []
  });

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/templates`);
      setCustomTemplates(response.data);
      // Merge system templates
      setTemplates([...SYSTEM_TEMPLATES, ...response.data]);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      // Even if API fails, still show system templates
      setTemplates(SYSTEM_TEMPLATES);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      language: 'en',
      country: '',
      channel: 'email',
      subject: '',
      body: '',
      placeholders: []
    });
    setEditingTemplate(null);
  };

  const handleOpenDialog = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        name: template.name,
        description: template.description || '',
        language: template.language || 'en',
        country: template.country || '',
        channel: template.channel,
        subject: template.subject || '',
        body: template.body,
        placeholders: template.placeholders || []
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handlePreview = (template) => {
    setPreviewTemplate(template);
    setPreviewOpen(true);
  };

  const extractPlaceholders = (text) => {
    const matches = text.match(/\{\{(\w+)\}\}/g) || [];
    return [...new Set(matches.map(m => m.replace(/\{\{|\}\}/g, '')))];
  };

  const handleBodyChange = (value) => {
    setFormData({
      ...formData,
      body: value,
      placeholders: extractPlaceholders(value + (formData.subject || ''))
    });
  };

  const handleSubjectChange = (value) => {
    setFormData({
      ...formData,
      subject: value,
      placeholders: extractPlaceholders(formData.body + value)
    });
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.body) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      
      if (editingTemplate && editingTemplate.id) {
        await axios.put(`${API_URL}/api/templates/${editingTemplate.id}`, formData);
        toast.success('Template updated successfully');
      } else {
        await axios.post(`${API_URL}/api/templates`, formData);
        toast.success('Template created successfully');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error('Failed to save template');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!templateToDelete) return;
    
    try {
      await axios.delete(`${API_URL}/api/templates/${templateToDelete.id}`);
      toast.success('Template deleted successfully');
      fetchTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
      toast.error('Failed to delete template');
    } finally {
      setDeleteConfirmOpen(false);
      setTemplateToDelete(null);
    }
  };

  const handleExport = () => {
    try {
      const exportData = {
        version: '1.0',
        exportDate: new Date().toISOString(),
        templates: templates.map(t => ({
          id: t.id,
          name: t.name,
          description: t.description,
          type: t.type || 'custom',
          channel: t.channel,
          language: t.language,
          country: t.country,
          subject: t.subject,
          body: t.body,
          placeholders: t.placeholders
        }))
      };
      
      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const filename = `edoverify-templates-${new Date().toISOString().split('T')[0]}.json`;
      
      // Create a temporary anchor element
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      
      // Append to body, click, and remove
      document.body.appendChild(a);
      a.click();
      
      // Cleanup after a short delay to ensure download starts
      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success(`Templates exported to ${filename}`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export templates');
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      if (!data.templates || !Array.isArray(data.templates)) {
        toast.error('Invalid template file format');
        return;
      }
      
      let importedCount = 0;
      for (const template of data.templates) {
        if (template.type === 'system') continue; // Skip system templates
        
        try {
          await axios.post(`${API_URL}/api/templates`, {
            name: template.name,
            description: template.description,
            channel: template.channel,
            language: template.language || 'en',
            country: template.country || '',
            subject: template.subject,
            body: template.body,
            placeholders: template.placeholders
          });
          importedCount++;
        } catch (err) {
          console.error('Failed to import template:', template.name, err);
        }
      }
      
      toast.success(`Imported ${importedCount} templates successfully`);
      fetchTemplates();
    } catch (error) {
      console.error('Failed to import templates:', error);
      toast.error('Failed to parse template file');
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const systemTemplates = templates.filter(t => t.type === 'system');
  const userTemplates = templates.filter(t => t.type !== 'system');
  
  const getChannelIcon = (channel) => {
    if (channel === 'sms') return Smartphone;
    return Mail;
  };

  const TemplateCard = ({ template, isSystem = false }) => {
    const ChannelIcon = getChannelIcon(template.channel);
    return (
      <Card className="hover:shadow-md transition-shadow">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <div className={`p-2 rounded-lg ${template.channel === 'sms' ? 'bg-blue-100' : isSystem ? 'bg-emerald-100' : 'bg-muted'}`}>
                <ChannelIcon className={`h-4 w-4 ${template.channel === 'sms' ? 'text-blue-600' : isSystem ? 'text-emerald-600' : ''}`} />
              </div>
              <div>
                <CardTitle className="text-base">{template.name}</CardTitle>
                <div className="flex items-center gap-2 mt-1">
                  {isSystem && (
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                      System
                    </Badge>
                  )}
                  <Badge variant={template.channel === 'sms' ? 'default' : 'secondary'} className={template.channel === 'sms' ? 'bg-blue-600' : ''}>
                    {template.channel.toUpperCase()}
                  </Badge>
                  {template.language && (
                    <Badge variant="outline">{template.language.toUpperCase()}</Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" onClick={() => handlePreview(template)} title="Preview">
                <Eye className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(template)} title="Edit">
                <Edit className="h-4 w-4" />
              </Button>
              {!isSystem && (
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => {
                    setTemplateToDelete(template);
                    setDeleteConfirmOpen(true);
                  }}
                  title="Delete"
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {template.description && (
            <p className="text-sm text-muted-foreground mb-2">{template.description}</p>
          )}
          {template.subject && (
            <p className="text-sm font-medium mb-2">
              <span className="text-muted-foreground">Subject: </span>
              {template.subject}
            </p>
          )}
          <p className="text-sm text-muted-foreground line-clamp-3 bg-slate-50 p-2 rounded border font-mono text-xs">
            {template.body.substring(0, 200)}...
          </p>
          {template.placeholders?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-3">
              {template.placeholders.map((p) => (
                <Badge 
                  key={p} 
                  variant="outline" 
                  className="text-xs font-mono cursor-pointer hover:bg-slate-100"
                  onClick={() => copyToClipboard(`{{${p}}}`)}
                  title="Click to copy"
                >
                  {`{{${p}}}`}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-6 space-y-6" data-testid="templates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Communication Templates</h1>
          <p className="text-muted-foreground mt-1">
            Manage email and SMS templates for verification communications
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <PageHelp pageKey="templates" />
          <Button variant="outline" size="sm" onClick={fetchTemplates}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
            <Upload className="h-4 w-4 mr-2" />
            Import
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImport}
            accept=".json"
            className="hidden"
          />
          <Button size="sm" onClick={() => handleOpenDialog()} data-testid="create-template-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Template
          </Button>
        </div>
      </div>

      {/* Templates Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="system" className="gap-2">
            <FileText className="h-4 w-4" />
            System Templates ({systemTemplates.length})
          </TabsTrigger>
          <TabsTrigger value="custom" className="gap-2">
            <Code className="h-4 w-4" />
            Custom Templates ({userTemplates.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="system" className="mt-6">
          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <Skeleton className="h-6 w-48 mb-4" />
                    <Skeleton className="h-20 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="space-y-6">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <p className="text-sm text-emerald-800">
                  <strong>System templates</strong> are used automatically by the verification workflow. 
                  You can customize their content, but the structure and placeholders should be preserved.
                </p>
              </div>
              
              {/* Email Templates */}
              <div>
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <Mail className="h-5 w-5 text-emerald-600" />
                  Email Templates
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {systemTemplates.filter(t => t.channel === 'email').map((template) => (
                    <TemplateCard key={template.id} template={template} isSystem={true} />
                  ))}
                </div>
              </div>
              
              {/* SMS Templates */}
              <div>
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <Smartphone className="h-5 w-5 text-blue-600" />
                  SMS Templates
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {systemTemplates.filter(t => t.channel === 'sms').map((template) => (
                    <TemplateCard key={template.id} template={template} isSystem={true} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="custom" className="mt-6">
          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {[1, 2].map((i) => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <Skeleton className="h-6 w-48 mb-4" />
                    <Skeleton className="h-20 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : userTemplates.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {userTemplates.map((template) => (
                <TemplateCard key={template.id} template={template} isSystem={false} />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <Mail className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <p className="text-lg font-medium">No custom templates</p>
                <p className="text-sm text-muted-foreground mt-1 mb-4">
                  Create custom templates for specific use cases
                </p>
                <Button onClick={() => handleOpenDialog()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Template
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit/Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? `Edit: ${editingTemplate.name}` : 'Create Template'}
            </DialogTitle>
            <DialogDescription>
              {editingTemplate 
                ? 'Update the template details below' 
                : 'Create a new email template'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Template Name <span className="text-red-500">*</span></Label>
                <Input 
                  id="name"
                  placeholder="e.g., Custom Reminder"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Channel</Label>
                <Select 
                  value={formData.channel} 
                  onValueChange={(v) => setFormData({ ...formData, channel: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="in_app">In-App</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input 
                id="description"
                placeholder="Brief description of when this template is used"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Language</Label>
                <Select 
                  value={formData.language} 
                  onValueChange={(v) => setFormData({ ...formData, language: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="fr">French</SelectItem>
                    <SelectItem value="es">Spanish</SelectItem>
                    <SelectItem value="pt">Portuguese</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Country (Optional)</Label>
                <Select 
                  value={formData.country || 'all'} 
                  onValueChange={(v) => setFormData({ ...formData, country: v === 'all' ? '' : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All countries" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Countries</SelectItem>
                    <SelectItem value="NG">Nigeria</SelectItem>
                    <SelectItem value="KE">Kenya</SelectItem>
                    <SelectItem value="GH">Ghana</SelectItem>
                    <SelectItem value="ZA">South Africa</SelectItem>
                    <SelectItem value="TZ">Tanzania</SelectItem>
                    <SelectItem value="UG">Uganda</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {formData.channel === 'email' && (
              <div className="space-y-2">
                <Label htmlFor="subject">Email Subject <span className="text-red-500">*</span></Label>
                <Input 
                  id="subject"
                  placeholder="Enter email subject - use {{placeholders}} for dynamic content"
                  value={formData.subject}
                  onChange={(e) => handleSubjectChange(e.target.value)}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="body">Message Body <span className="text-red-500">*</span></Label>
              <Textarea 
                id="body"
                placeholder="Enter message content. Use {{placeholder}} for dynamic variables."
                rows={12}
                value={formData.body}
                onChange={(e) => handleBodyChange(e.target.value)}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Common placeholders: {"{{applicant_name}}"}, {"{{verification_link}}"}, {"{{case_id}}"}, {"{{deadline}}"}
              </p>
            </div>

            {formData.placeholders.length > 0 && (
              <div className="space-y-2">
                <Label>Detected Placeholders</Label>
                <div className="flex flex-wrap gap-1">
                  {formData.placeholders.map((p) => (
                    <Badge key={p} variant="secondary" className="font-mono">
                      {`{{${p}}}`}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Save className="h-4 w-4 mr-2" />
              {editingTemplate ? 'Save Changes' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Template Preview: {previewTemplate?.name}</DialogTitle>
            <DialogDescription>
              {previewTemplate?.description || 'Preview how this template will appear'}
            </DialogDescription>
          </DialogHeader>
          
          {previewTemplate && (
            <ScrollArea className="max-h-[60vh]">
              <div className="space-y-4">
                {previewTemplate.subject && (
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">SUBJECT</Label>
                    <div className="bg-slate-100 p-3 rounded-md font-medium">
                      {previewTemplate.subject}
                    </div>
                  </div>
                )}
                
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">BODY</Label>
                  <div className="bg-white border rounded-md p-4 whitespace-pre-wrap font-sans text-sm leading-relaxed">
                    {previewTemplate.body}
                  </div>
                </div>

                {previewTemplate.placeholders?.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">PLACEHOLDERS USED</Label>
                    <div className="flex flex-wrap gap-1">
                      {previewTemplate.placeholders.map((p) => (
                        <Badge 
                          key={p} 
                          variant="outline" 
                          className="font-mono cursor-pointer"
                          onClick={() => copyToClipboard(`{{${p}}}`)}
                        >
                          {`{{${p}}}`}
                          <Copy className="h-3 w-3 ml-1" />
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>
              Close
            </Button>
            <Button onClick={() => {
              setPreviewOpen(false);
              handleOpenDialog(previewTemplate);
            }}>
              <Edit className="h-4 w-4 mr-2" />
              Edit Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{templateToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default Templates;
